import { lazy, Suspense } from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { AppLayout } from '@/shared/components/layouts/AppLayout';
import { LoadingScreen } from '@/shared/components/common/LoadingScreen';
import { ProtectedRoute } from '@/features/auth/components/ProtectedRoute';
import { GuestRoute } from '@/features/auth/components/GuestRoute';

// ─── Auth Pages (lazy) ─────────────────────────────────────────────

const LoginPage = lazy(() => import('@/features/auth/pages/LoginPage'));
const RegisterPage = lazy(() => import('@/features/auth/pages/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('@/features/auth/pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/features/auth/pages/ResetPasswordPage'));
const SessionExpiredPage = lazy(() => import('@/features/auth/pages/SessionExpiredPage'));
const UnauthorizedPage = lazy(() => import('@/features/auth/pages/UnauthorizedPage'));

// ─── App Pages (lazy) ──────────────────────────────────────────────

const DashboardPage = lazy(() => import('@/pages/dashboard/DashboardPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));
const NotificationsPage = lazy(() => import('@/pages/NotificationsPage'));
const ReportsPage = lazy(() => import('@/pages/reports/ReportsPage'));

// ─── Expense Pages (lazy) ──────────────────────────────────────────

const ExpenseListPage = lazy(() => import('@/pages/expenses/ExpenseListPage'));
const ExpenseDetailPage = lazy(() => import('@/pages/expenses/ExpenseDetailPage'));
const CreateExpensePage = lazy(() => import('@/pages/expenses/CreateExpensePage'));
const EditExpensePage = lazy(() => import('@/pages/expenses/EditExpensePage'));
const ErpPushPage = lazy(() => import('@/pages/expenses/ErpPushPage'));

// ─── Profile Pages (lazy) ─────────────────────────────────────────

const ProfilePage = lazy(() => import('@/pages/profile/ProfilePage'));
const ChangePasswordPage = lazy(() => import('@/pages/profile/ChangePasswordPage'));
const SecurityPage = lazy(() => import('@/pages/profile/SecurityPage'));
const SessionsPage = lazy(() => import('@/pages/profile/SessionsPage'));
const PreferencesPage = lazy(() => import('@/pages/profile/PreferencesPage'));

// ─── Expense Source Selection Pages (lazy) ────────────────────────

const ChooseExpenseSourcePage = lazy(() => import('@/pages/expenses/ChooseExpenseSourcePage'));
const UploadExpensePage = lazy(() => import('@/pages/expenses/UploadExpensePage'));
const WhatsAppInfoPage = lazy(() => import('@/pages/expenses/WhatsAppInfoPage'));
const EmailInfoPage = lazy(() => import('@/pages/expenses/EmailInfoPage'));

// ─── Expense Processing Pages (lazy) ──────────────────────────────

const ExpenseProcessingPage = lazy(() => import('@/pages/expenses/ExpenseProcessingPage'));
const ExpenseProcessingDetailsPage = lazy(() => import('@/pages/expenses/ExpenseProcessingDetailsPage'));

// ─── Approval Pages (lazy) ─────────────────────────────────────────

const ApprovalQueuePage = lazy(() => import('@/pages/approvals/ApprovalQueuePage'));
const ExpenseReviewPage = lazy(() => import('@/pages/approvals/ExpenseReviewPage'));

// ─── Admin Pages (lazy) ────────────────────────────────────────────

const CategoryPage = lazy(() => import('@/pages/admin/CategoryPage'));
const MerchantPage = lazy(() => import('@/pages/admin/MerchantPage'));
const PaymentMethodPage = lazy(() => import('@/pages/admin/PaymentMethodPage'));
const CompanySettingsPage = lazy(() => import('@/pages/admin/CompanySettingsPage'));
const UsersPage = lazy(() => import('@/pages/admin/UsersPage'));

function SuspenseWrapper({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<LoadingScreen />}>{children}</Suspense>;
}

const router = createBrowserRouter([
  // ─── Guest Routes (unauthenticated users only) ───────────────────
  {
    element: <GuestRoute />,
    children: [
      {
        path: '/login',
        element: <SuspenseWrapper><LoginPage /></SuspenseWrapper>,
      },
      {
        path: '/register',
        element: <SuspenseWrapper><RegisterPage /></SuspenseWrapper>,
      },
      {
        path: '/forgot-password',
        element: <SuspenseWrapper><ForgotPasswordPage /></SuspenseWrapper>,
      },
    ],
  },

  // ─── Public Routes (always accessible) ───────────────────────────
  // /reset-password lives here, NOT under GuestRoute: it's reached from a
  // token in an emailed link, which must work even if the browser
  // currently has an active session (GuestRoute would otherwise bounce an
  // already-logged-in user straight to /dashboard before they ever see
  // the reset form — defeating the entire point of the emailed link).
  {
    path: '/reset-password',
    element: <SuspenseWrapper><ResetPasswordPage /></SuspenseWrapper>,
  },
  {
    path: '/session-expired',
    element: <SuspenseWrapper><SessionExpiredPage /></SuspenseWrapper>,
  },
  {
    path: '/unauthorized',
    element: <SuspenseWrapper><UnauthorizedPage /></SuspenseWrapper>,
  },

  // ─── Protected Routes (authenticated users only) ─────────────────
  {
    element: <ProtectedRoute />,
    children: [
      {
        path: '/',
        element: <AppLayout />,
        children: [
          { index: true, element: <Navigate to="/dashboard" replace /> },
          { path: 'dashboard', element: <SuspenseWrapper><DashboardPage /></SuspenseWrapper> },
          { path: 'notifications', element: <SuspenseWrapper><NotificationsPage /></SuspenseWrapper> },
          { path: 'reports', element: <SuspenseWrapper><ReportsPage /></SuspenseWrapper> },

          // ─── Expense Routes ──────────────────────────────────
          { path: 'expenses', element: <SuspenseWrapper><ExpenseListPage /></SuspenseWrapper> },
          { path: 'expenses/choose-source', element: <SuspenseWrapper><ChooseExpenseSourcePage /></SuspenseWrapper> },
          { path: 'expenses/erp-push', element: <SuspenseWrapper><ErpPushPage /></SuspenseWrapper> },
          { path: 'expenses/new', element: <SuspenseWrapper><CreateExpensePage /></SuspenseWrapper> },
          { path: 'expenses/upload', element: <SuspenseWrapper><UploadExpensePage /></SuspenseWrapper> },
          { path: 'expenses/whatsapp', element: <SuspenseWrapper><WhatsAppInfoPage /></SuspenseWrapper> },
          { path: 'expenses/email', element: <SuspenseWrapper><EmailInfoPage /></SuspenseWrapper> },
          { path: 'expenses/processing', element: <SuspenseWrapper><ExpenseProcessingPage /></SuspenseWrapper> },
          { path: 'expenses/processing/:id', element: <SuspenseWrapper><ExpenseProcessingDetailsPage /></SuspenseWrapper> },
          { path: 'expenses/:id', element: <SuspenseWrapper><ExpenseDetailPage /></SuspenseWrapper> },
          { path: 'expenses/:id/edit', element: <SuspenseWrapper><EditExpensePage /></SuspenseWrapper> },

          // ─── Profile Routes ─────────────────────────────────
          { path: 'profile', element: <SuspenseWrapper><ProfilePage /></SuspenseWrapper> },
          { path: 'profile/change-password', element: <SuspenseWrapper><ChangePasswordPage /></SuspenseWrapper> },
          { path: 'profile/security', element: <SuspenseWrapper><SecurityPage /></SuspenseWrapper> },
          { path: 'profile/sessions', element: <SuspenseWrapper><SessionsPage /></SuspenseWrapper> },
          { path: 'profile/preferences', element: <SuspenseWrapper><PreferencesPage /></SuspenseWrapper> },

          // ─── Approval Routes ────────────────────────────────
          { path: 'approvals', element: <SuspenseWrapper><ApprovalQueuePage /></SuspenseWrapper> },
          { path: 'approvals/:id', element: <SuspenseWrapper><ExpenseReviewPage /></SuspenseWrapper> },

          // ─── Admin Routes ────────────────────────────────────
          { path: 'users', element: <SuspenseWrapper><UsersPage /></SuspenseWrapper> },
          { path: 'categories', element: <SuspenseWrapper><CategoryPage /></SuspenseWrapper> },
          { path: 'merchants', element: <SuspenseWrapper><MerchantPage /></SuspenseWrapper> },
          { path: 'payment-methods', element: <SuspenseWrapper><PaymentMethodPage /></SuspenseWrapper> },
          { path: 'settings', element: <SuspenseWrapper><CompanySettingsPage /></SuspenseWrapper> },

          { path: '*', element: <SuspenseWrapper><NotFoundPage /></SuspenseWrapper> },
        ],
      },
    ],
  },

  // ─── Catch-all ───────────────────────────────────────────────────
  {
    path: '*',
    element: <SuspenseWrapper><NotFoundPage /></SuspenseWrapper>,
  },
]);

export function Router() {
  return <RouterProvider router={router} />;
}
